/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { configure } from "axios-hooks";

import { cookieRE } from "src/authentication/withXCSRFToken.ts";

/*
 Http client that handles baseUrl, Authorization header (bearer token) and default headers
 The aim of this class is to centralize all http requests behavior in the app
 We will add later interceptors to log all requests/responses for better monitoring
 For tests we will use adapters. see `axios-mock-adapter`

 Usage:
  Perform a GET request by overriding some params and headers
  `HttpClient.instance.get("/users", {params: {ID: "1234"}, headers: {Accept: "image/*"}}`

  Update the token for all requests
  `HttpClient.instance.setToken(bearer)`
*/

export class HttpClient {
  private axiosClient: AxiosInstance;
  private static httpClient: HttpClient | null = null;

  private constructor() {
    this.axiosClient = axios.create({
      baseURL: "",
      timeout: 30000,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
  }

  // Singleton
  getAxiosInstance() {
    // returns the existing instance or creates one
    return this.axiosClient;
  }

  static get instance() {
    // returns the existing instance or creates one
    return (this.httpClient = this.httpClient ?? new HttpClient());
  }

  setToken = (token: string | null) => {
    // settings auth header for all requests
    this.axiosClient = axios.create({
      baseURL: "",
      timeout: 30000,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      },
    });
    this.setInterceptor();
    configure({ axios: this.axiosClient, cache: false });
  };

  setInterceptor() {
    this.axiosClient.interceptors.request.use((config) => {
      const matches = document.cookie.match(cookieRE);
      if (matches) {
        config.headers["X-CSRF-Token"] = matches[1];
      }
      return config;
    });
  }
  get<T>(path: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosClient.get<T, AxiosResponse<T>>(path, config);
  }

  async post<T>(path: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosClient.post<T, AxiosResponse<T>>(path, data, config);
  }

  delete<T>(path: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosClient.delete(path, config);
  }
}
